import React from 'react';
import { Search, Globe, Radio, Database, Plus } from 'lucide-react';
import { Button } from '../components/ui/button';
import { LoadingOverlay } from '../components/ui/LoadingOverlay';
import { useLoading } from '../hooks/useLoading';
import { sleep } from '../utils/sleep';

const Discovery = () => {
  const { isLoading, withLoading } = useLoading();

  const handleStartScan = async (type: string) => {
    await withLoading(async () => {
      // Simulate API call
      await sleep(3000);
      console.log(`Starting ${type} scan...`);
    });
  };

  return (
    <div className="space-y-6 relative">
      {isLoading && <LoadingOverlay message="Starting scan..." />}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Search className="h-8 w-8 text-blue-500" />
          <div>
            <h1 className="text-2xl font-bold text-white">Asset Discovery</h1>
            <p className="text-sm text-gray-400">Discover and monitor your digital assets</p>
          </div>
        </div>
        <Button>
          <Plus className="h-4 w-4 mr-2" />
          New Scan
        </Button>
      </div>

      {/* Discovery Methods */}
      <div className="grid grid-cols-3 gap-6">
        <div className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
          <div className="flex items-center space-x-3 mb-4">
            <Globe className="h-6 w-6 text-blue-500" />
            <h2 className="text-lg font-semibold text-white">Domain Discovery</h2>
          </div>
          <p className="text-sm text-gray-400 mb-4">
            Discover subdomains and web assets associated with your domains.
          </p>
          <Button 
            variant="outline" 
            className="w-full"
            onClick={() => handleStartScan('domain')}
          >
            Start Domain Scan
          </Button>
        </div>

        <div className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
          <div className="flex items-center space-x-3 mb-4">
            <Radio className="h-6 w-6 text-purple-500" />
            <h2 className="text-lg font-semibold text-white">Network Discovery</h2>
          </div>
          <p className="text-sm text-gray-400 mb-4">
            Scan IP ranges to discover network assets and open ports.
          </p>
          <Button 
            variant="outline" 
            className="w-full"
            onClick={() => handleStartScan('network')}
          >
            Start Network Scan
          </Button>
        </div>

        <div className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
          <div className="flex items-center space-x-3 mb-4">
            <Database className="h-6 w-6 text-emerald-500" />
            <h2 className="text-lg font-semibold text-white">Cloud Discovery</h2>
          </div>
          <p className="text-sm text-gray-400 mb-4">
            Discover assets across your cloud providers and services.
          </p>
          <Button 
            variant="outline" 
            className="w-full"
            onClick={() => handleStartScan('cloud')}
          >
            Start Cloud Scan
          </Button>
        </div>
      </div>

      {/* Recent Scans */}
      <div className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
        <h2 className="text-lg font-semibold text-white mb-4">Recent Scans</h2>
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-center justify-between p-4 bg-gray-800/30 rounded-lg">
              <div>
                <div className="text-sm font-medium text-white">Full Asset Discovery Scan</div>
                <div className="text-xs text-gray-400 mt-1">Started 2 hours ago • Completed in 45 minutes</div>
              </div>
              <div className="flex items-center space-x-3">
                <span className="text-xs text-emerald-500">124 assets discovered</span>
                <Button variant="outline" size="sm">View Results</Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Discovery;